import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTimes, FaFileUpload, FaSpinner, FaCheck, FaExclamationTriangle,
  FaChevronRight, FaImage, FaBoxOpen, FaSyncAlt,
} from 'react-icons/fa';
import {
  createProductImportPreview,
  updateProductImportRows,
  commitProductImport,
  cancelProductImport,
  getProductImport,
} from '../services/apiService';
import { formatMoneyCHF } from '../utils/priceUtils';

/**
 * ProductImportModal — admin product manager
 *
 * Three-step workflow (mirrors DiscountImportModal):
 *   1. Upload a .csv/.xlsx file of pharmacodes (optional PPUB column)
 *   2. Preview: rows classified into new / existing / invalid; toggle which to
 *      import and optionally set a PPUB per row
 *   3. Commit: the backend runs the import engine in the BACKGROUND, so we poll
 *      GET /product-imports/:id until it reports 'committed' or 'failed', then
 *      show the per-row result summary.
 *
 * Backend: POST /product-imports → PATCH /product-imports/:id/rows →
 *          POST /product-imports/:id/commit (202) → poll GET /product-imports/:id.
 */

const BUCKET_LABELS = {
  newProduct: { label: 'New — will be fetched & added', tone: 'green' },
  existing:   { label: 'Already in catalog — will refresh', tone: 'blue' },
  invalid:    { label: 'Invalid pharmacode (skipped)', tone: 'red' },
};

const TONE = {
  green: 'bg-green-50 text-green-700 border-green-200',
  blue:  'bg-blue-50 text-blue-700 border-blue-200',
  red:   'bg-red-50 text-red-700 border-red-200',
};

const SELECTABLE = new Set(['newProduct', 'existing']);
const POLL_MS = 2000;

const chf = (n) => (n != null && Number.isFinite(n) ? formatMoneyCHF(n) : '—');

const ProductImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'committing' | 'done'

  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const [importDoc, setImportDoc] = useState(null);
  const [activeBucket, setActiveBucket] = useState('newProduct');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const pollRef = useRef(null);

  const clearPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  // reset whenever modal opens; stop polling whenever it closes
  useEffect(() => {
    if (!isOpen) { clearPoll(); return; }
    setStep('upload');
    setFile(null);
    setImportDoc(null);
    setActiveBucket('newProduct');
    setBusy(false);
    setError(null);
  }, [isOpen, clearPoll]);

  useEffect(() => () => clearPoll(), [clearPoll]); // unmount cleanup

  const onFileChange = (e) => { setFile(e.target.files?.[0] || null); setError(null); };
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0] || null;
    if (f) { setFile(f); setError(null); }
  };

  const apiErr = (e) => e?.response?.data?.message || e?.response?.data?.error || e.message || 'Something went wrong';

  const submitUpload = useCallback(async () => {
    if (!file) { setError('Pick a file first'); return; }
    setBusy(true); setError(null);
    try {
      const r = await createProductImportPreview(file);
      setImportDoc(r.data);
      setStep('preview');
      const s = r.data?.summary || {};
      const firstNonEmpty = ['newProduct', 'existing', 'invalid'].find((b) => (s[b] || 0) > 0) || 'newProduct';
      setActiveBucket(firstNonEmpty);
    } catch (e) {
      setError(apiErr(e));
    } finally {
      setBusy(false);
    }
  }, [file]);

  // Persist a set of row edits ({ pharmacode|rawCode, selected?, ppub? }).
  const pushRowEdits = useCallback(async (rows) => {
    if (!importDoc || rows.length === 0) return;
    setBusy(true); setError(null);
    try {
      const r = await updateProductImportRows(importDoc._id, rows);
      setImportDoc(r.data);
    } catch (e) {
      setError(apiErr(e));
    } finally {
      setBusy(false);
    }
  }, [importDoc]);

  const toggleRow = useCallback((row, selected) => {
    pushRowEdits([{ rawCode: row.rawCode, pharmacode: row.pharmacode, selected }]);
  }, [pushRowEdits]);

  const setRowPpub = useCallback((row, ppub) => {
    pushRowEdits([{ rawCode: row.rawCode, pharmacode: row.pharmacode, ppub }]);
  }, [pushRowEdits]);

  const toggleAllInBucket = useCallback((bucket, selected) => {
    if (!importDoc) return;
    const rows = importDoc.rows
      .filter((r) => r.bucket === bucket)
      .map((r) => ({ rawCode: r.rawCode, pharmacode: r.pharmacode, selected }));
    pushRowEdits(rows);
  }, [importDoc, pushRowEdits]);

  // Begin polling for a committing import.
  const startPolling = useCallback((id) => {
    clearPoll();
    pollRef.current = setInterval(async () => {
      try {
        const r = await getProductImport(id);
        const d = r.data;
        setImportDoc(d);
        if (d.status !== 'committing') {
          clearPoll();
          setStep('done');
          if (typeof onSuccess === 'function') onSuccess(d);
        }
      } catch (e) {
        // transient poll error — keep trying; surface only the message
        setError(apiErr(e));
      }
    }, POLL_MS);
  }, [clearPoll, onSuccess]);

  const submitCommit = useCallback(async () => {
    if (!importDoc) return;
    setBusy(true); setError(null);
    try {
      const r = await commitProductImport(importDoc._id);
      setImportDoc(r.data);
      setStep('committing');
      startPolling(importDoc._id);
    } catch (e) {
      setError(apiErr(e));
    } finally {
      setBusy(false);
    }
  }, [importDoc, startPolling]);

  const submitCancel = useCallback(async () => {
    clearPoll();
    if (importDoc && importDoc.status === 'previewed') {
      try { await cancelProductImport(importDoc._id); } catch { /* best effort */ }
    }
    onClose();
  }, [importDoc, onClose, clearPoll]);

  const summary = importDoc?.summary || {};
  const rowsInBucket = useMemo(
    () => (importDoc ? importDoc.rows.filter((r) => r.bucket === activeBucket) : []),
    [importDoc, activeBucket]
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget && step !== 'committing') submitCancel(); }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FaFileUpload className="text-emerald-600" />
                Import products from file
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Upload a CSV / XLSX / PDF of pharmacodes · preview · import from HCI
              </p>
            </div>
            <button
              onClick={submitCancel}
              disabled={step === 'committing'}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>

          {/* Step indicator */}
          <div className="px-5 py-3 flex items-center gap-2 text-xs text-gray-500 border-b border-gray-100 bg-gray-50/40">
            <Step n={1} label="Upload" active={step === 'upload'} done={step !== 'upload'} />
            <FaChevronRight className="text-gray-300 w-3 h-3" />
            <Step n={2} label="Preview" active={step === 'preview'} done={step === 'committing' || step === 'done'} />
            <FaChevronRight className="text-gray-300 w-3 h-3" />
            <Step n={3} label="Import" active={step === 'committing' || step === 'done'} done={false} />
          </div>

          {error && (
            <div className="px-5 py-3 bg-red-50 border-b border-red-200 text-red-700 text-sm flex items-center gap-2">
              <FaExclamationTriangle /> {error}
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {step === 'upload' && (
              <UploadStep file={file} onFileChange={onFileChange} onDrop={onDrop} fileInputRef={fileInputRef} />
            )}

            {step === 'preview' && importDoc && (
              <PreviewStep
                importDoc={importDoc}
                summary={summary}
                activeBucket={activeBucket}
                setActiveBucket={setActiveBucket}
                rowsInBucket={rowsInBucket}
                onToggleRow={toggleRow}
                onSetPpub={setRowPpub}
                onToggleAll={toggleAllInBucket}
                busy={busy}
              />
            )}

            {(step === 'committing' || step === 'done') && importDoc && (
              <ResultStep step={step} importDoc={importDoc} summary={summary} />
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-500">
              {step === 'preview' && (
                <>
                  <span className="font-medium text-emerald-700">{summary.selected || 0}</span> selected to import
                  {(summary.invalid || 0) > 0 && (
                    <> · <span className="font-medium text-red-700">{summary.invalid}</span> invalid</>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={submitCancel}
                disabled={step === 'committing'}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-40"
              >
                {step === 'done' ? 'Close' : 'Cancel'}
              </button>
              {step === 'upload' && (
                <button
                  onClick={submitUpload}
                  disabled={!file || busy}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {busy ? <FaSpinner className="animate-spin" /> : <FaChevronRight />}
                  Parse & preview
                </button>
              )}
              {step === 'preview' && (
                <button
                  onClick={submitCommit}
                  disabled={busy || (summary.selected || 0) === 0}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {busy ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                  Import {summary.selected || 0} product{(summary.selected || 0) === 1 ? '' : 's'}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ── sub-components ───────────────────────────────────────────────────

const Step = ({ n, label, active, done }) => (
  <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
    active ? 'bg-emerald-100 text-emerald-700' : done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
  }`}>
    <span className={`w-4 h-4 rounded-full inline-flex items-center justify-center text-[10px] ${
      active ? 'bg-emerald-600 text-white' : done ? 'bg-green-600 text-white' : 'bg-gray-300 text-white'
    }`}>{done ? <FaCheck className="w-2 h-2" /> : n}</span>
    {label}
  </span>
);

const UploadStep = ({ file, onFileChange, onDrop, fileInputRef }) => (
  <div className="p-6 space-y-5">
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      onClick={() => fileInputRef.current?.click()}
      className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition"
    >
      <FaFileUpload className="mx-auto text-3xl text-gray-400 mb-2" />
      {file ? (
        <div>
          <p className="text-sm font-medium text-gray-900">{file.name}</p>
          <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB · click to replace</p>
        </div>
      ) : (
        <div>
          <p className="text-sm font-medium text-gray-900">Drop a CSV, XLSX or PDF file here</p>
          <p className="text-xs text-gray-500 mt-1">or click to browse</p>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.pdf,text/csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={onFileChange}
        className="hidden"
      />
    </div>

    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800 space-y-1">
      <p className="font-medium">Expected format</p>
      <ul className="list-disc list-inside text-blue-700 text-xs space-y-0.5">
        <li><span className="font-medium">CSV / XLSX:</span> first column <span className="font-mono">pharmacode</span>, optional second column <span className="font-mono">PPUB</span></li>
        <li><span className="font-medium">PDF:</span> Galexis Verfalldaten export — pharmacodes are extracted automatically</li>
        <li>A header row is auto-detected; leading zeros are fine</li>
        <li>New pharmacodes are fetched from HCI; existing ones are refreshed</li>
      </ul>
    </div>
  </div>
);

const PreviewStep = ({ importDoc, summary, activeBucket, setActiveBucket, rowsInBucket, onToggleRow, onSetPpub, onToggleAll, busy }) => {
  const selectable = SELECTABLE.has(activeBucket);
  const allSelected = selectable && rowsInBucket.length > 0 && rowsInBucket.every((r) => r.selected);

  return (
    <div className="flex flex-col h-full">
      {/* Warnings */}
      {Array.isArray(importDoc.warnings) && importDoc.warnings.length > 0 && (
        <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs">
          {importDoc.warnings.map((w, i) => <div key={i}>• {w}</div>)}
        </div>
      )}

      {/* Bucket tabs */}
      <div className="flex flex-wrap gap-2 px-5 py-3 bg-gray-50/60 border-b border-gray-200">
        {Object.entries(BUCKET_LABELS).map(([key, info]) => {
          const count = summary[key] || 0;
          if (count === 0 && key !== 'newProduct') return null;
          const isActive = activeBucket === key;
          return (
            <button
              key={key}
              onClick={() => setActiveBucket(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                isActive ? TONE[info.tone] + ' ring-2 ring-offset-1 ring-emerald-300' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {info.label} <span className="ml-1 font-bold">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Row table */}
      <div className="flex-1 overflow-y-auto">
        {rowsInBucket.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-400">No rows in this group.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide sticky top-0">
              <tr>
                <th className="text-left px-4 py-2 font-medium w-10">
                  {selectable && (
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => onToggleAll(activeBucket, e.target.checked)}
                      disabled={busy}
                      aria-label="Select all in group"
                    />
                  )}
                </th>
                <th className="text-left px-4 py-2 font-medium">Pharmacode</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-right px-4 py-2 font-medium">Current PPUB</th>
                <th className="text-left px-4 py-2 font-medium">Set PPUB</th>
                <th className="text-center px-4 py-2 font-medium">Images</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rowsInBucket.map((row) => (
                <RowDisplay
                  key={row.rawCode}
                  row={row}
                  selectable={selectable}
                  onToggle={onToggleRow}
                  onSetPpub={onSetPpub}
                  busy={busy}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const RowDisplay = ({ row, selectable, onToggle, onSetPpub, busy }) => {
  const [draftPpub, setDraftPpub] = useState(row.ppub ?? '');
  useEffect(() => { setDraftPpub(row.ppub ?? ''); }, [row.ppub]);

  const commitPpub = () => {
    const trimmed = draftPpub === '' ? null : Number(draftPpub);
    const current = row.ppub ?? null;
    if (trimmed !== current) onSetPpub(row, trimmed);
  };

  return (
    <tr className={`hover:bg-gray-50/60 ${row.bucket === 'invalid' ? 'opacity-60' : ''}`}>
      <td className="px-4 py-2">
        {selectable && (
          <input
            type="checkbox"
            checked={!!row.selected}
            onChange={(e) => onToggle(row, e.target.checked)}
            disabled={busy}
          />
        )}
      </td>
      <td className="px-4 py-2 font-mono text-xs text-gray-700">{row.pharmacode ?? row.rawCode}</td>
      <td className="px-4 py-2">
        {row.bucket === 'newProduct' && (
          <span className="inline-flex items-center gap-1 text-green-700 text-xs"><FaBoxOpen className="w-3 h-3" /> New</span>
        )}
        {row.bucket === 'existing' && (
          <span className="inline-flex items-center gap-1 text-blue-700 text-xs"><FaSyncAlt className="w-3 h-3" /> In catalog</span>
        )}
        {row.bucket === 'invalid' && (
          <span className="inline-flex items-center gap-1 text-red-600 text-xs"><FaExclamationTriangle className="w-3 h-3" /> Invalid code</span>
        )}
      </td>
      <td className="px-4 py-2 text-right text-gray-500">{chf(row.currentPPUB)}</td>
      <td className="px-4 py-2">
        {selectable ? (
          <input
            type="number"
            step="0.01"
            min="0"
            value={draftPpub}
            onChange={(e) => setDraftPpub(e.target.value)}
            onBlur={commitPpub}
            placeholder="—"
            disabled={busy || !row.selected}
            className="w-24 text-xs px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50"
          />
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      <td className="px-4 py-2 text-center">
        {row.bucket === 'existing' ? (
          row.hasImages
            ? <FaImage className="inline w-3.5 h-3.5 text-green-500" title="Has images" />
            : <span className="text-[10px] text-amber-600">none</span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
    </tr>
  );
};

const ResultStep = ({ step, importDoc, summary }) => {
  if (step === 'committing') {
    return (
      <div className="p-10 text-center space-y-4">
        <FaSpinner className="mx-auto text-3xl text-emerald-500 animate-spin" />
        <div>
          <h3 className="text-lg font-bold text-gray-900">Importing products…</h3>
          <p className="text-sm text-gray-500 mt-1">
            Fetching {summary.selected || 0} product{(summary.selected || 0) === 1 ? '' : 's'} from HCI and indexing. This can take a few minutes — you can keep this open.
          </p>
        </div>
      </div>
    );
  }

  const failed = importDoc.status === 'failed';
  return (
    <div className="p-8 text-center space-y-4">
      <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${failed ? 'bg-red-100' : 'bg-green-100'}`}>
        {failed ? <FaExclamationTriangle className="text-red-600 text-2xl" /> : <FaCheck className="text-green-600 text-2xl" />}
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-900">{failed ? 'Import failed' : 'Product import complete'}</h3>
        <p className="text-sm text-gray-500 mt-1">
          <span className="font-semibold text-green-700">{summary.importedSuccess || 0}</span> imported
          {(summary.importedFailed || 0) > 0 && (
            <>{' · '}<span className="font-semibold text-red-700">{summary.importedFailed}</span> failed</>
          )}
        </p>
        {importDoc?.engine?.reindexed && (
          <p className="text-xs text-gray-400 mt-1">
            Reindexed {importDoc.engine.reindexed.indexed || 0} / {importDoc.engine.reindexed.matched || 0} matched in search.
          </p>
        )}
      </div>
      {(failed || (summary.importedFailed || 0) > 0) && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 max-w-lg mx-auto text-left">
          {failed
            ? 'The import engine reported an error. The product list was not changed for the failed rows.'
            : 'Some rows could not be imported (pharmacode not found in HCI, or fetch failed).'}
          {importDoc?.engine?.stderr && (
            <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap text-[10px] text-gray-600">{importDoc.engine.stderr.slice(-1500)}</pre>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductImportModal;
